import React, { useState, useEffect, useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Briefcase, MessageSquare, LogOut, User, Bookmark, Bell, Star } from 'lucide-react';
import useResponsive from '../../hooks/useResponsive';
import { AuthContext } from '../../context/AuthContext';
import api from '../../api/api';

const CandidateSidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useContext(AuthContext);
  const [isCollapsed, setIsCollapsed] = useState(window.innerWidth < 1024);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [jobAlerts, setJobAlerts] = useState(0);
  
  // Handle window resize for responsive sidebar
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setIsCollapsed(true);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch unread messages and job alerts count
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        // Fetch actual unread message count
        const messagesRes = await api.get('/messages/unread-count');
        if (messagesRes.data && typeof messagesRes.data.count === 'number') {
          setUnreadMessages(messagesRes.data.count);
        }
        
        // Fetch actual job alerts count
        const alertsRes = await api.get('/job-alerts/count');
        if (alertsRes.data && typeof alertsRes.data.count === 'number') {
          setJobAlerts(alertsRes.data.count);
        }
      } catch (error) {
        console.error('Error fetching notifications:', error);
        // Keep badges at 0 on error instead of showing mock data
        setUnreadMessages(0);
        setJobAlerts(0);
      }
    };
    
    fetchNotifications();
    // Refresh every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const navItems = [
    { icon: <Briefcase size={20} />, name: 'Jobs', path: '/jobs', badge: null },
    // { icon: <FileText size={20} />, name: 'Applications', path: '/candidate/applications', badge: null }, // REMOVED
    { icon: <Bookmark size={20} />, name: 'Saved Jobs', path: '/candidate/saved-jobs', badge: null },
    { icon: <MessageSquare size={20} />, name: 'Inbox', path: '/candidate/messages', badge: unreadMessages },
    { icon: <Bell size={20} />, name: 'Job Alerts', path: '/candidate/job-alerts', badge: jobAlerts },
    // Reviews page removed
    // Support page removed
  ];

  const NavItem = ({ icon, name, path, badge }) => {
    const isActive = location.pathname === path;
    return (
      <Link
        to={path}
        title={name}
        className={`group relative flex items-center justify-center py-1.5 my-0.5 transition-all duration-200 ${isActive ? 'text-black font-medium' : 'text-gray-500 hover:text-black'}`}
      >
        {React.cloneElement(icon, { size: 20, className: 'text-current' })}
        {badge > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center shadow-md animate-pulse">
            {badge > 9 ? '9+' : badge}
          </span>
        )}
      </Link>
    );
  };

  // NavSection component removed as it's no longer needed

  const { isMobile } = useResponsive();
  
  // Don't render the sidebar on mobile
  if (isMobile) return null;
  
  return (
    <aside
      className="flex flex-col h-[calc(70vh-2rem)] bg-white shadow-[0_10px_30px_-5px_rgba(0,0,0,0.15),_0_5px_15px_-3px_rgba(0,0,0,0.08)] w-14 z-20 rounded-3xl mx-4 fixed top-1/2 left-4 transform -translate-y-1/2"
      style={{ boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)' }}
    >
      {/* Sidebar Header */}
      <div className="flex items-center justify-center h-10 mb-1 mt-1">
        <Link to="/jobs" className="flex items-center justify-center w-full">
          <span className="text-lg font-bold text-black">
            J.
          </span>
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 flex flex-col items-center py-2 overflow-y-auto">
        {navItems.map((item, index) => (
          <NavItem key={index} {...item} />
        ))}
      </nav>

      {/* User Profile */}
      <div className="py-1 mt-auto mb-1">        
        <div className="w-5 h-[1px] bg-gray-300 mb-1 mx-auto"></div>
        <Link
          to="/candidate/profile"
          title="Profile"
          className="flex items-center justify-center py-1.5 my-0.5 transition-all duration-200 text-gray-500 hover:text-black"
        >
          <User size={20} className="text-current" />
        </Link>
        
        <button
          onClick={() => {
            logout();
            navigate('/');
          }}
          title="Logout"
          className="flex items-center justify-center py-1.5 my-0.5 transition-all duration-200 text-gray-500 hover:text-black w-full"
        >
          <LogOut size={20} className="text-current" />
        </button>
      </div>
    </aside>
  );
};

export default CandidateSidebar;
